import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
// Removed Cookies usage; rely on useAuth()
import { getImageUrl } from '../config/runtime';
import useUnifiedWebSocket from '../hooks/useUnifiedWebSocket';
import { projectAPI } from '../services/api';
import { X, Users, Clock, AlertTriangle, Play } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ParticipantNotificationManager } from '../components/ParticipantNotification';
import ProjectVictoryModal from '../components/ProjectVictoryModal';

export default function WaitingRoomPage() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  const [participants, setParticipants] = useState([]);
  const [viewerCount, setViewerCount] = useState(1); // Start at 1 (myself)
  const [serverTimeOffsetMs, setServerTimeOffsetMs] = useState(0);
  const [nowMs, setNowMs] = useState(Date.now());
  const [participantNotifications, setParticipantNotifications] = useState([]);
  const [isVictoryModalOpen, setIsVictoryModalOpen] = useState(false);
  const [victoryWinner, setVictoryWinner] = useState(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const lastVictoryAtRef = useRef(0);
  const hasAutoStartedRef = useRef(false);

  const showVictory = (winner) => {
    const now = Date.now();
    if (now - (lastVictoryAtRef.current || 0) < 1200) return;
    lastVictoryAtRef.current = now;
    if (winner) setVictoryWinner(winner);
    setIsVictoryModalOpen(true);
  };
  
  // Unified WebSocket manages connection lifecycle; no per-page socket ref needed

  // Fetch project details
  const { data: projectResponse, isLoading: projectLoading, error: projectError } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectAPI.getById(projectId),
    enabled: !!projectId,
    refetchInterval: (query) => {
      // If countdown is near 0, poll more frequently to catch status transition
      if (!query?.state?.data) {
        return 5000; // Default if query not ready yet
      }
      const project = query.state.data?.data?.project || query.state.data?.data || query.state.data;
      if (project?.status === 'waiting' && project?.scheduledStartAt) {
        const startTs = new Date(project.scheduledStartAt).getTime();
        const remaining = startTs - Date.now();
        // Poll every 1 second when within 10 seconds of start time
        if (remaining > 0 && remaining < 10000) {
          return 1000;
        }
      }
      return 5000; // Default: refresh every 5 seconds
    },
    retry: (failureCount, error) => {
      // Don't retry on 404 (project deleted)
      if (error?.response?.status === 404) {
        console.log('❌ Project not found (404), stopping retries');
        return false;
      }
      return failureCount < 3;
    },
  });
  
  // Redirect to /explore if project is deleted (404)
  useEffect(() => {
    if (projectError?.response?.status === 404) {
      console.log('🚪 Project deleted (404), redirecting to /explore');
      toast.error('This project no longer exists');
      
      // Remove queries to prevent refetch attempts
      queryClient.removeQueries(['project', projectId]);
      queryClient.removeQueries(['participants', projectId]);
      queryClient.removeQueries(['activeProject']);
      
      navigate('/projects', { replace: true });
    }
  }, [projectError, navigate, projectId, queryClient]);

  const project = projectResponse?.data?.project || projectResponse?.data;
  
  // Redirect if project is not in waiting state
  useEffect(() => {
    // IMPORTANT: Strict equality check to prevent race conditions
    // If we are in the waiting room, the status MUST be 'waiting'
    // If it's 'pending', it means we might be in a transition state or loaded wrong data
    if (project && project.status !== 'waiting') {
      console.log(`[WaitingRoom] Project status is '${project.status}', checking redirection rules...`);
      
      if (project.status === 'ongoing') {
        // Project started! Navigate to project page (chat)
        console.log('🚀 Project started (ongoing), navigating to project page');
        navigate(`/projects/${projectId}`);
      } else if (project.status === 'finished') {
        // Project finished
        console.log('🏁 Project finished, navigating to project page');
        navigate(`/projects/${projectId}`);
      } else if (project.status === 'pending') {
        // EDGE CASE: If we just transitioned from Pending -> Waiting, the API might still return Pending for a split second
        // OR we might have been redirected here by mistake.
        // We should NOT immediately redirect back to /projects/:id (which is the config page)
        // because that creates a redirect loop if the mutation just succeeded.
        
        console.log('⏳ Project status is still pending. This might be a race condition.');
        // Allow a grace period or check if we have a valid future start time
        // If we have a scheduled start time, we might legitimately be waiting
      } else {
        // Other states go back to project config
        console.log(`⚠️ Unexpected state '${project.status}', navigating to project page`);
        navigate(`/projects/${projectId}`);
      }
    }
  }, [project, projectId, navigate]);

  // Real-time countdown
  useEffect(() => {
    const t = setInterval(() => {
      setNowMs(Date.now());
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const getCountdownText = () => {
    if (!project?.scheduledStartAt) return null;
    const startTs = new Date(project.scheduledStartAt).getTime();
    const remaining = Math.max(0, startTs - (nowMs + serverTimeOffsetMs));
    
    // If less than 0, it's "Ready" or "0:00"
    if (remaining <= 0) return '00:00';
    
    const totalSec = Math.floor(remaining / 1000);
    const hours = Math.floor(totalSec / 3600);
    const mins = Math.floor((totalSec % 3600) / 60);
    const secs = totalSec % 60;
    
    if (hours > 0) {
      return `${hours}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const getCountdownLabel = () => {
    if (!project?.scheduledStartAt) return 'Waiting Room';
    const startTs = new Date(project.scheduledStartAt).getTime();
    const remaining = startTs - (nowMs + serverTimeOffsetMs);
    if (remaining <= 0) return 'Starting project...';
    return 'Project starts in';
  };

  // WebSocket for real-time presence and events (unified)
  const unified = useUnifiedWebSocket(null);
  useEffect(() => {
    if (!projectId || !project?.isMultiplayer) return;
    const channel = `project_${projectId}`;
    const unsubscribe = unified.subscribeToChannel(channel);
    const offJoined = unified.registerHandler('viewer_joined', (data) => {
      setViewerCount(v => v + 1);
      if (data?.serverTime) {
        const serverNow = new Date(data.serverTime).getTime();
        const clientNow = Date.now();
        setServerTimeOffsetMs(serverNow - clientNow);
      }
    }, channel);
    const offLeft = unified.registerHandler('viewer_left', () => setViewerCount(v => Math.max(0, v - 1)), channel);
    const offParticipantJoined = unified.registerHandler('participant_joined', (data) => {
      console.log('🔔 WebSocket: participant_joined', data);
      if (Array.isArray(data?.participants)) {
        console.log('🔄 Setting participants from WebSocket (joined):', data.participants);
        setParticipants(data.participants);
        // Update query cache to prevent API poll from overwriting
        queryClient.setQueryData(['participants', projectId], { data: { participants: data.participants } });
      }
      if (data?.user) setParticipantNotifications(prev => ([...prev, { user: data.user, action: 'joined', id: Date.now() }]));
    }, channel);
    const offParticipantLeft = unified.registerHandler('participant_left', (data) => {
      console.log('🔔 WebSocket: participant_left', data);
      if (Array.isArray(data?.participants)) {
        console.log('🔄 Setting participants from WebSocket (left):', data.participants);
        setParticipants(data.participants);
        // Update query cache to prevent API poll from overwriting
        queryClient.setQueryData(['participants', projectId], { data: { participants: data.participants } });
      }
      if (data?.user) setParticipantNotifications(prev => ([...prev, { user: data.user, action: 'left', id: Date.now() }]));
    }, channel);
    const offCancelled = unified.registerHandler('project_cancelled', (data) => {
      const reason = data?.reason || 'Project was cancelled';
      toast.error(reason);
      setTimeout(() => navigate('/explore', { replace: true }), 1500);
    }, channel);
    const offStarted = unified.registerHandler('project_started', () => navigate(`/projects/${projectId}`), channel);
    const offFinished = unified.registerHandler('project_finished', (data) => {
      const isCompetitive = project?.gameMode === 'competitive' && project?.isMultiplayer;
      if (isCompetitive) {
        try {
          const winnerUser = data?.winner || data?.projectWinner || null;
          if (winnerUser) showVictory(winnerUser);
          else {
            projectAPI.getLeaderboard(projectId).then((lb) => {
              const leaderboard = lb?.data?.data?.leaderboard || lb?.data?.leaderboard || [];
              showVictory(leaderboard[0]?.user || null);
            }).catch(() => showVictory(null));
          }
        } catch (_) { showVictory(null); }
      } else {
        showVictory(null);
      }
    }, channel);
    return () => {
      try { offJoined && offJoined(); offLeft && offLeft(); offParticipantJoined && offParticipantJoined(); offParticipantLeft && offParticipantLeft(); offCancelled && offCancelled(); offStarted && offStarted(); offFinished && offFinished(); } catch (_) {}
      try { unsubscribe && unsubscribe(); } catch (_) {}
    };
  }, [projectId, project?.isMultiplayer, project?.gameMode, navigate, unified, queryClient]);

  // Fetch participants
  const { data: participantsResponse } = useQuery({
    queryKey: ['participants', projectId],
    queryFn: () => projectAPI.getParticipants(projectId),
    enabled: !!projectId && !!project?.isMultiplayer,
    refetchInterval: 5000, // Reduced frequency: poll every 5s for real-time updates (in case WebSocket misses)
    retry: (failureCount, error) => {
      // Don't retry on 404 (project deleted)
      if (error?.response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  });

  useEffect(() => {
    // Handle both response formats from backend
    // Only update if we don't already have participants from WebSocket (to prevent race conditions)
    let newParticipants = null;
    if (participantsResponse?.data?.data?.participants) {
      newParticipants = participantsResponse.data.data.participants;
    } else if (participantsResponse?.data?.participants) {
      newParticipants = participantsResponse.data.participants;
    }
    
    if (newParticipants) {
      console.log('📋 Participants fetched from API:', newParticipants);
      // Only update if different from current state (prevents overwriting WebSocket updates)
      setParticipants(prev => {
        const prevIds = prev.map(p => p.id).sort().join(',');
        const newIds = newParticipants.map(p => p.id).sort().join(',');
        if (prevIds !== newIds) {
          return newParticipants;
        }
        return prev;
      });
    }
  }, [participantsResponse]);

  const currentUserId = user?.id;
  const isParticipant = participants.some(p => p.id === currentUserId);
  const isOwner = project?.ownerId === currentUserId;
  
  // Check if project can be joined (capacity check)
  // maxParticipants = total team size including owner, so available slots = max - 1 (owner) - current collaborators
  const isFull = project?.maxParticipants && (1 + participants.length) >= project.maxParticipants;
  
  // Debug logging
  useEffect(() => {
    if (project && currentUserId) {
      console.log('🔍 Waiting Room State:', {
        currentUserId,
        projectOwnerId: project.ownerId,
        isOwner,
        isParticipant,
        participantCount: participants.length,
        maxParticipants: project.maxParticipants,
        isFull,
        userObject: user,
        originalProjectId: project.originalProjectId,
        projectName: project.name
      });
    }
  }, [project, currentUserId, isOwner, isParticipant, participants, isFull, user]);

  // Cancel/Delete project mutation (owner only)
  const cancelProjectMutation = useMutation({
    mutationFn: async () => {
      return await projectAPI.delete(projectId);
    },
    onSuccess: () => {
      console.log('✅ Project deleted successfully, cleaning up and navigating');
      
      // Invalidate ProjectsPage queries
      queryClient.invalidateQueries({ queryKey: ['user-projects'] });
      
      // REMOVE ALL queries for this project immediately (including activeProject!)
      console.log('🗑️ Removing all queries for deleted project');
      queryClient.removeQueries(['project', projectId]);
      queryClient.removeQueries(['participants', projectId]);
      queryClient.removeQueries(['activeProject']); // CRITICAL: Remove cached activeProject
      queryClient.removeQueries(['projects']); // Remove projects list cache
      
      // Navigate away
      console.log('🚀 Navigating to /projects');
      navigate('/projects', { replace: true });
      
      // Refetch fresh data after navigation
      setTimeout(() => {
        console.log('🔄 Refetching fresh project lists');
        queryClient.invalidateQueries(['projects']);
        queryClient.invalidateQueries(['activeProject']);
      }, 100);
    },
    onError: (error) => {
      console.error('❌ Failed to delete project:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel project');
    }
  });

  // Join mutation
  const joinMutation = useMutation({
    mutationFn: async () => {
      const res = await projectAPI.join(projectId);
      const srv = res?.data?.data?.serverTime;
      if (srv) {
        const serverNow = new Date(srv).getTime();
        setServerTimeOffsetMs(serverNow - Date.now());
      }
      return res.data;
    },
    onSuccess: (data) => {
      // toast.success('Joined project!');
      console.log('✅ Join success, response:', data);
      if (data?.data?.participants) {
        console.log('🔄 Setting participants from join response:', data.data.participants);
        setParticipants(data.data.participants);
      }
      // Force immediate refetch of participants
      queryClient.invalidateQueries(['participants', projectId]);
      queryClient.refetchQueries(['participants', projectId]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to join project');
    }
  });

  // Leave mutation
  const leaveMutation = useMutation({
    mutationFn: async () => {
      return await projectAPI.leave(projectId);
    },
    onSuccess: (data) => {
      // toast.success('You left the project');
      if (data?.data?.data?.participants) {
        setParticipants(data.data.data.participants);
      }
      queryClient.invalidateQueries(['participants', projectId]);
      // Navigate back to explore after leaving
      setTimeout(() => navigate('/explore', { replace: true }), 1000);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to leave project');
    }
  });

  // Start Project mutation (owner only)
  const startProjectMutation = useMutation({
    mutationFn: async () => {
      // 1. First update the scheduled start time to NOW to bypass backend "future start" check
      // This allows owners to start a project early (before the originally scheduled time)
      await projectAPI.update(projectId, { 
        scheduledStartAt: new Date().toISOString(),
        // We also ensure startNow is true to be explicit
        startNow: true 
      });
      
      // 2. Then update status to ongoing
      return await projectAPI.updateStatus(projectId, { status: 'ongoing' });
    },
    onSuccess: () => {
      console.log('✅ Project started manually by owner');
      // Navigation handled by WebSocket event or effect
      // navigate(`/projects/${projectId}`);
    },
    onError: (error) => {
      console.error('❌ Failed to start project:', error);
      toast.error(error.response?.data?.detail || 'Failed to start project');
    }
  });

  // Auto-start mutation (can be triggered by any participant when timer ends)
  const autoStartMutation = useMutation({
    mutationFn: async () => {
      // Just update status - backend will verify if scheduled time has passed
      return await projectAPI.updateStatus(projectId, { status: 'ongoing' });
    },
    onSuccess: () => {
      console.log('✅ Auto-start signal sent successfully');
    },
    onError: (error) => {
      // Ignore errors (another client might have started it first)
      console.log('⚠️ Auto-start signal skipped or failed:', error?.message);
    }
  });

  // Auto-start trigger for ANY participant
  useEffect(() => {
    // Must be a scheduled waiting event (or pending if we are stuck there)
    // We strictly check that a schedule exists
    if (!project?.scheduledStartAt) return;
    
    // Only participants or owner can trigger the start (backend restriction)
    if (!isOwner && !isParticipant) return;

    const startTs = new Date(project.scheduledStartAt).getTime();
    // Use server offset to be precise
    const remaining = startTs - (nowMs + serverTimeOffsetMs);
    
    // If timer is done (with 1s buffer to ensure UI hits 0:00 first)
    if (remaining <= -1000 && !hasAutoStartedRef.current) {
      console.log('⏰ Timer finished. Attempting to auto-start project...');
      hasAutoStartedRef.current = true;
      
      // Add a small random delay (0-3s) to prevent all clients from hitting API simultaneously
      // Owners start immediately-ish (0-1s), others wait a bit longer
      const delay = isOwner ? Math.random() * 1000 : 1000 + Math.random() * 3000;
      
      setTimeout(() => {
        autoStartMutation.mutate();
      }, delay);
    }
  }, [nowMs, isOwner, isParticipant, project?.scheduledStartAt, project?.status, serverTimeOffsetMs, autoStartMutation]);

  const handleGoBack = () => {
    navigate('/projects');
  };

  if (projectLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white dark:bg-gray-900">
        <div className="text-gray-600 dark:text-gray-400">Loading waiting room...</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex items-center justify-center h-screen bg-white dark:bg-gray-900">
        <div className="text-gray-600 dark:text-gray-400">Project not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-slate-50 dark:bg-slate-950 relative overflow-hidden flex flex-col font-sans text-slate-900 dark:text-white transition-colors duration-500">
      {/* Dynamic Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-purple-400/20 dark:bg-purple-600/30 rounded-full blur-[100px] animate-pulse mix-blend-multiply dark:mix-blend-screen" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-blue-400/20 dark:bg-blue-600/30 rounded-full blur-[100px] animate-pulse delay-1000 mix-blend-multiply dark:mix-blend-screen" />
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10 dark:opacity-20 brightness-100 contrast-150 mix-blend-overlay" />
      </div>

      {/* Navbar / Top Actions */}
      <div className="relative z-50 px-6 py-6 flex justify-between items-center">
        <button
          onClick={handleGoBack}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-white/60 dark:bg-white/10 hover:bg-white/80 dark:hover:bg-white/20 backdrop-blur-md border border-slate-200 dark:border-white/10 transition-all active:scale-95 shadow-sm"
        >
          <X size={20} className="text-slate-700 dark:text-white/90" />
        </button>
        <div className="px-3 py-1 rounded-full bg-white/60 dark:bg-white/5 border border-slate-200 dark:border-white/10 backdrop-blur-sm flex items-center gap-2 shadow-sm">
          <div className={`w-2 h-2 rounded-full ${viewerCount > 0 ? 'bg-green-500 dark:bg-green-400 animate-pulse' : 'bg-gray-400'}`} />
          <span className="text-xs font-medium text-slate-600 dark:text-white/70">{viewerCount} online</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 pb-12 z-10 relative">
        
        {/* Title Section - Reordered to Top */}
        <div className="mb-8 text-center max-w-xl mx-auto">
          <h1 className="text-2xl md:text-4xl font-bold text-slate-900 dark:text-white tracking-tight leading-tight mb-2">
            {project.name}
          </h1>
          {project.description && (
             <p className="text-sm text-slate-500 dark:text-slate-400 line-clamp-2 px-4">{project.description}</p>
          )}
        </div>

        {/* Timer Section */}
        <div className="mb-12 text-center">
           <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-100 dark:bg-indigo-500/20 border border-indigo-200 dark:border-indigo-500/30 backdrop-blur-sm mb-4">
            <Clock className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-300" />
            <span className="text-xs font-bold tracking-wider uppercase text-indigo-700 dark:text-indigo-200">{getCountdownLabel()}</span>
          </div>
          
          <div className="relative group cursor-default">
            {/* Glow effect */}
            <div className="absolute -inset-4 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
            <div className="relative text-7xl sm:text-8xl md:text-9xl font-black font-mono tracking-tighter text-slate-900 dark:text-white drop-shadow-2xl select-none tabular-nums">
              {getCountdownText() || '--:--'}
            </div>
          </div>
        </div>

        {/* Participants Grid - Mobile First */}
        <div className="w-full max-w-sm mx-auto mb-10">
          <div className="flex items-center justify-between mb-4 px-2">
            <h3 className="text-sm font-medium text-slate-500 dark:text-white/60 uppercase tracking-wider text-xs">Team Squad</h3>
            <span className="text-xs font-mono text-slate-400 dark:text-white/40">
              {participants.length}/{project.maxParticipants || '∞'}
            </span>
          </div>
          
          {participants.length > 0 ? (
            <div className="grid grid-cols-5 gap-3 justify-items-center">
              {participants.slice(0, 10).map(p => (
                <div key={p.id} className="relative group">
                  <div className="w-12 h-12 rounded-2xl bg-white/80 dark:bg-white/10 backdrop-blur-sm border border-slate-200 dark:border-white/10 p-0.5 transition-transform hover:-translate-y-1 hover:scale-110 hover:border-indigo-400/50 hover:shadow-lg hover:shadow-indigo-500/20">
                    {p.avatar ? (
                      <img 
                        src={getImageUrl(p.avatar)} 
                        alt={p.username}
                        className="w-full h-full rounded-[14px] object-cover"
                      />
                    ) : (
                      <div className="w-full h-full rounded-[14px] bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-xs font-bold text-white">
                        {p.username?.[0] || 'U'}
                      </div>
                    )}
                  </div>
                  <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-slate-900/90 dark:bg-black/80 text-white text-[10px] px-2 py-1 rounded-md whitespace-nowrap pointer-events-none z-20">
                    {p.username}
                  </div>
                </div>
              ))}
               {/* Empty slots placeholders */}
               {Array.from({ length: Math.max(0, Math.min(4, (project.maxParticipants || 50) - participants.length)) }).map((_, i) => (
                  <div key={`empty-${i}`} className="w-12 h-12 rounded-2xl border-2 border-dashed border-slate-300 dark:border-white/5 flex items-center justify-center bg-white/20 dark:bg-transparent">
                      <Users className="w-4 h-4 text-slate-400 dark:text-white/10" />
                  </div>
               ))}
            </div>
          ) : (
             <div className="py-6 text-center rounded-2xl border border-dashed border-slate-300 dark:border-white/10 bg-white/40 dark:bg-white/5">
                <p className="text-sm text-slate-500 dark:text-white/30">Waiting for squad...</p>
             </div>
          )}
        </div>

        {/* Primary Actions - Sticky Bottom or prominent */}
        <div className="w-full max-w-sm mx-auto space-y-3 z-20">
             {isOwner ? (
                 <>
                    <button 
                      onClick={() => startProjectMutation.mutate()}
                      disabled={startProjectMutation.isLoading}
                      className="w-full h-14 bg-slate-900 dark:bg-white hover:bg-slate-800 dark:hover:bg-gray-50 text-white dark:text-black font-bold text-lg rounded-2xl shadow-xl shadow-indigo-500/10 dark:shadow-white/10 transition-all active:scale-95 flex items-center justify-center gap-2"
                    >
                       {startProjectMutation.isLoading ? (
                           <div className="w-5 h-5 border-2 border-white/30 dark:border-black/30 border-t-white dark:border-t-black rounded-full animate-spin" />
                       ) : (
                           <>
                             <Play className="w-5 h-5 fill-white dark:fill-black" />
                             <span>Start Project Now</span>
                           </>
                       )}
                    </button>
                    <div className="grid grid-cols-2 gap-3">
                        <button 
                           onClick={() => setIsDeleteConfirmOpen(true)}
                           className="h-12 rounded-xl bg-red-50 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/20 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium text-sm transition-colors border border-red-200 dark:border-red-500/10"
                        >
                           Cancel Project
                        </button>
                        <button 
                           onClick={() => navigate('/projects')}
                           className="h-12 rounded-xl bg-white/60 dark:bg-white/5 hover:bg-white dark:hover:bg-white/10 text-slate-600 dark:text-white/70 hover:text-slate-900 dark:hover:text-white font-medium text-sm transition-colors border border-slate-200 dark:border-white/5"
                        >
                           Leave Room
                        </button>
                    </div>
                 </>
             ) : !isParticipant ? (
                 <button 
                    onClick={() => joinMutation.mutate()}
                    disabled={isFull}
                    className="w-full h-14 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-lg rounded-2xl shadow-xl shadow-indigo-500/20 transition-all active:scale-95 flex items-center justify-center gap-2"
                 >
                    {isFull ? 'Project Full' : 'Join Squad'}
                 </button>
             ) : (
                 <div className="space-y-3">
                     <div className="h-14 w-full flex items-center justify-center gap-2 rounded-2xl bg-emerald-100 dark:bg-emerald-500/20 border border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300 font-bold text-lg animate-pulse">
                        <span className="w-2 h-2 bg-emerald-500 dark:bg-emerald-400 rounded-full animate-ping" />
                        Ready to Deploy
                     </div>
                     <button 
                        onClick={() => leaveMutation.mutate()}
                        className="w-full h-12 rounded-xl bg-white/60 dark:bg-white/5 hover:bg-white dark:hover:bg-white/10 text-slate-500 dark:text-white/50 hover:text-slate-800 dark:hover:text-white/80 font-medium text-sm transition-colors border border-slate-200 dark:border-white/5"
                     >
                        Leave Squad
                     </button>
                 </div>
             )}
        </div>
      </div>

      {/* Participant Notifications Toast */}
      <ParticipantNotificationManager notifications={participantNotifications} />

      {/* Victory Modal */}
      {project && isVictoryModalOpen && (
        <ProjectVictoryModal
          isOpen={isVictoryModalOpen}
          project={project}
          winner={victoryWinner}
          isYouWinner={victoryWinner?.id === user?.id}
          currentUser={user}
          onClose={() => setIsVictoryModalOpen(false)}
        />
      )}

      {/* Delete Confirmation Modal (Themed) */}
      {isDeleteConfirmOpen && (
        <div className="fixed inset-0 bg-slate-900/20 dark:bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-white/10 rounded-3xl p-6 max-w-xs w-full shadow-2xl transform scale-100 animate-in fade-in zoom-in duration-200">
            <div className="w-12 h-12 bg-red-100 dark:bg-red-500/20 rounded-full flex items-center justify-center mb-4 mx-auto">
              <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white text-center mb-2">Delete Project?</h3>
            <p className="text-slate-500 dark:text-white/50 text-center text-sm mb-6">
              This cannot be undone. All data and participants will be removed.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setIsDeleteConfirmOpen(false)}
                className="py-3 bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 rounded-xl text-slate-700 dark:text-white font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setIsDeleteConfirmOpen(false);
                  cancelProjectMutation.mutate();
                }}
                className="py-3 bg-red-600 hover:bg-red-500 rounded-xl text-white font-bold transition-colors shadow-lg shadow-red-600/20"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
